/**
 * Google Analytics 4 w trybie "Basic" Consent Mode v2.
 *
 * Skrypt gtag.js NIE jest ladowany, dopoki uzytkownik nie kliknie "Zgoda"
 * w banerze cookies. Bez zgody do Google nie trafia zadne zapytanie
 * i nie powstaja ciasteczka _ga.
 */

export const GA_MEASUREMENT_ID = "G-YMFC207QVZ";
export const CHOICE_COOKIE = "MediniceCookieConsent";
export const COOKIE_MAX_AGE = 60 * 60 * 24 * 60;

type ConsentChoice = "allow" | "deny";

const DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;
const isEnabled = process.env.NODE_ENV === "production";

export const readConsentChoice = (): ConsentChoice | null => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CHOICE_COOKIE}=(allow|deny)`)
  );
  return match ? (match[1] as ConsentChoice) : null;
};

export const saveConsentChoice = (choice: ConsentChoice) => {
  document.cookie = `${CHOICE_COOKIE}=${choice}; path=/; SameSite=Lax; Secure; max-age=${COOKIE_MAX_AGE}`;
};

export const loadAnalytics = () => {
  if (!isEnabled) return;
  const w = window as any;

  // gtag.js juz wczytany w tej sesji (np. zgoda cofnieta i wyrazona ponownie)
  if (typeof w.gtag === "function") {
    w[DISABLE_FLAG] = false;
    w.gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }

  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // gtag.js wymaga obiektu `arguments`, nie tablicy
    w.dataLayer.push(arguments);
  };

  w.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  w.gtag("consent", "update", { analytics_storage: "granted" });
  w.gtag("js", new Date());
  w.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

export const removeAnalyticsCookies = () => {
  const names = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name.startsWith("_ga"));
  if (!names.length) return;

  // _ga jest zwykle zapisywane na domenie nadrzednej (np. .medinice.pl)
  const parts = window.location.hostname.split(".");
  const domains = parts
    .slice(0, -1)
    .map((_, i) => parts.slice(i).join("."));

  names.forEach((name) => {
    document.cookie = `${name}=; path=/; max-age=0`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; path=/; domain=${domain}; max-age=0`;
    });
  });
};

export const disableAnalytics = () => {
  const w = window as any;
  w[DISABLE_FLAG] = true;
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", { analytics_storage: "denied" });
  }
  removeAnalyticsCookies();
};

export const trackPageView = (location: Location) => {
  const w = window as any;
  if (!isEnabled || typeof w.gtag !== "function" || w[DISABLE_FLAG]) return;

  // dwa razy requestAnimationFrame -- zeby useDocumentTitle zdazyl ustawic tytul
  requestAnimationFrame(() =>
    requestAnimationFrame(() =>
      w.gtag("event", "page_view", {
        page_path: location.pathname + location.search + location.hash,
      })
    )
  );
};
